import React from 'react'
import {useIntl} from 'react-intl'
import {useDispatch} from 'react-redux'
import {Summary} from '@findhotel/sapi/dist/types/packages/core/src/insights'
import {ReviewCommentWrapper} from 'components/Reviews/ReviewCommentWrapper'
import {reviewMessages} from 'components/Reviews/ReviewMessages'
import {getLengthOfStay} from 'utils'

import {Box} from '@vio/atlas/helpers/Box'
import {Button} from '@vio/atlas/src/components/designSystem/Button'
import {Divider} from '@vio/atlas/src/components/designSystem/Divider'
import {Icon} from '@vio/atlas/src/components/designSystem/Icon'
import {Text} from '@vio/atlas/src/components/designSystem/Text'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {ReviewSource} from '@vio/shared/src/search/ReviewComment'

import {SelectedElementType} from '../../ReviewsSection'
import {AiPoweredReviewsFeedback} from '../AiPoweredReviewsFeedback'
import {aiReviewsMessages} from '../constants'
import {getCategoryIcon, getCategoryTitle} from '../utils'

interface Props {
  categoryDetails?: Summary
  onShowAllReviews: () => void
}
export const AiPoweredSummaryItem: React.FC<Props> = ({
  categoryDetails,
  onShowAllReviews
}) => {
  const dispatch = useDispatch()
  const {formatMessage} = useIntl()

  const onSourceLogoClick = () => {
    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.ReviewDetails,
        action: Action.Clicked,
        payload: {selectedElement: SelectedElementType.ReviewLogo}
      })
    )
  }

  if (!categoryDetails) return null

  return (
    <Box
      marginBottom="109px"
      desktopXs={{marginBottom: 's000'}}
      data-id={`AiPoweredSummaryItem-${categoryDetails.category}`}
    >
      <Box
        borderRadius="lg"
        padding="s600"
        backgroundColor="background.brand.tertiary"
      >
        <Box display="flex" gap="s300" alignItems="center" paddingBottom="s500">
          <Icon name={getCategoryIcon(categoryDetails.category)} size="lg" />
          <Text variant="titleXL" tag="p">
            {formatMessage(getCategoryTitle(categoryDetails.category))}
          </Text>
        </Box>
        <Text variant="bodyM">{categoryDetails.summary}</Text>
      </Box>
      <Box paddingBottom="s500" paddingTop="s700">
        <Text variant="titleL" tag="p">
          <FormattedMessageWrapper {...aiReviewsMessages.relatedGuestReviews} />
        </Text>
      </Box>
      <Box display="flex" flexDirection="column" gap="s500">
        {categoryDetails.reviews.map((review, index) => (
          <React.Fragment
            key={[review.reviewDate, review.reviewerName, index].join('-')}
          >
            {index > 0 && <Divider />}
            <ReviewCommentWrapper
              reviewText={review.text}
              reviewLanguage={review.language}
              reviewerScore={review.reviewerScore}
              reviewDate={review.reviewDate}
              lengthOfStay={getLengthOfStay(review.checkIn, review.checkOut)}
              reviewerName={review.reviewerName}
              onSourceLogoClick={onSourceLogoClick}
              SourceLogoSlot={
                review?.reviewSource && (
                  <ReviewSource providerCode={review.reviewSource} />
                )
              }
              reviewSource={review.reviewSource}
              truncatedContentLines={5}
            />
          </React.Fragment>
        ))}
      </Box>
      <Box
        paddingTop="s600"
        display="flex"
        flexDirection="column"
        alignItems="center"
      >
        <Button variant="tertiary" onClick={onShowAllReviews} size="lg">
          <FormattedMessageWrapper {...reviewMessages.readAllReviews} />
        </Button>
      </Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        paddingTop="s500"
        gap="s500"
        flexDirection="column"
        desktopXs={{flexDirection: 'row'}}
      >
        <AiPoweredReviewsFeedback sourceComponent={categoryDetails.title} />
        <Box display="flex" alignItems="center" gap="s100">
          <Text variant="bodyXS" colorPath="content.neutral.secondary">
            {formatMessage(
              aiReviewsMessages.aiPoweredSummarySectionDescription
            )}
          </Text>
          <Icon
            name="Sparkles"
            size="sm"
            colorPath="content.neutral.secondary"
          />
        </Box>
      </Box>
    </Box>
  )
}
